// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { Paths } from '@shared/data-access/interfaces/routes/paths';
import { useDispatch } from 'react-redux';
import { usePagination } from '@shared/ui-kit/components/grouped-table';
import { exportTableToExcel, renameKeys } from '@shared/ui-kit/utils';
import { useSafeUpdate } from '@shared/ui-kit/hooks';
import { useCallback, useEffect, useState } from 'react';
import { fetchFundHoldings } from '../utils/fetch-fund-holdings';
import type { FundHoldingDetail } from '@shared/data-access/interfaces/api/ai/commons';

export interface LoadPosition {
  readonly ticker: string;
  readonly weight: number;
}

export type AnalyzePopup = (
  positions: LoadPosition[],
  redirectPath: Paths,
) => LoadPosition[];

interface UseAssetProfileTopHoldingsProps {
  readonly ticker: string;
  readonly assetType: string;
  readonly analyzePopup?: AnalyzePopup;
}

export const useAssetProfileTopHoldings = ({
  assetType,
  ticker,
  analyzePopup,
}: UseAssetProfileTopHoldingsProps) => {
  const dispatch = useDispatch();
  const runSafeUpdate = useSafeUpdate();

  const [holdings, setHoldings] = useState<FundHoldingDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const pagination = usePagination({
    pageCount: 1,
    pageIndex: 0,
    pageSize: 10,
  });
  /* istanbul ignore next */
  const getFundHoldings = useCallback(
    async (page: number, assets_page: number) => {
      try {
        const payload = { assetType, ticker, page, assets_page };
        const data = await fetchFundHoldings(payload, dispatch);
        return data;
      } catch {
        return { holdings: [], pages: 1 };
      }
    },
    [assetType, dispatch, ticker],
  );

  /**
   * If the user clicks on show more button, fetch all the holdings otherwise return
   * the current holdings
   */
  const fetchAllHoldings = useCallback(async () => {
    // istanbul ignore next
    if (pagination.pageSize > 10) {
      const { holdings } = await getFundHoldings(1, 9999);
      return holdings;
    }
    return holdings;
  }, [getFundHoldings, holdings, pagination.pageSize]);
  /* istanbul ignore next */
  const handleDownloadHoldings = useCallback(async () => {
    try {
      const currentHoldings = await fetchAllHoldings();
      const filename = `${ticker}_holdings`;
      const data = currentHoldings.map(holding =>
        renameKeys(holding, { percentage: 'outstanding_balance' }),
      );
      exportTableToExcel(data, filename);
    } catch (error) {
      console.error('Error downloading fund holdings', error);
    }
  }, [fetchAllHoldings, ticker]);
  /* istanbul ignore next */
  const handleAnalyzeHoldings = useCallback(async () => {
    try {
      const currentHoldings = await fetchAllHoldings();
      const positions = currentHoldings.map(holding => ({
        ticker: holding.ticker,
        weight: holding.percentage / 100,
      }));

      analyzePopup?.(positions, '/analysis/stock');
    } catch (error) {
      console.error('Error analyzing fund holdings', error);
    }
  }, [fetchAllHoldings, analyzePopup]);

  useEffect(() => {
    // istanbul ignore next
    const fetchHoldings = async () => {
      try {
        runSafeUpdate(() => setLoading(true));
        const { pageIndex, pageSize } = pagination;
        const { holdings, pages } = await getFundHoldings(pageIndex, pageSize);
        setHoldings(holdings);
        pagination.onPaginationCountChange(pages);
      } catch (error) {
        console.error('Error fetching fund holdings', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHoldings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize, assetType, ticker]);

  return {
    pagination,
    holdings,
    loading,
    onDownloadHoldings: handleDownloadHoldings,
    onAnalyzeHoldings: handleAnalyzeHoldings,
  };
};
